import React from "react";
import { Download, Smartphone, ShieldCheck, Star, Apple } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { API_BASE } from "@/lib/api";
import api from "@/lib/api";

export default function DownloadPage() {
  const [links, setLinks] = React.useState([]);

  React.useEffect(() => {
    api.get("/downloads").then((r) => setLinks(r.data)).catch(() => {});
  }, []);

  const get = (platform) => links.find((l) => l.platform === platform);

  return (
    <div data-testid="download-page">
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(14,165,233,0.16),transparent_55%),radial-gradient(circle_at_bottom_left,rgba(212,175,55,0.12),transparent_55%)]" />
        <div className="mfb-container relative grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="overline mb-4">My Funding Bank App</div>
            <h1 className="font-[Outfit] text-5xl sm:text-6xl font-light tracking-tighter text-white leading-[1.05]">
              Your wealth, <span className="gradient-text-sky">in your pocket</span>.
            </h1>
            <p className="mt-5 text-slate-300 leading-relaxed text-base max-w-xl">
              Trade, invest, monitor portfolios, redeem gold and book travel — all from one beautifully designed mobile app.
            </p>

            <div className="mt-9 flex flex-wrap gap-3">
              <a
                href={get("ios")?.url || "#"}
                data-testid="download-ios"
                onClick={() => trackEvent("download_app_click", { platform: "ios" })}
                className="inline-flex items-center gap-3 rounded-2xl bg-white text-black px-5 py-3 hover:opacity-90 transition"
              >
                <Apple className="w-6 h-6" />
                <div className="leading-tight text-left">
                  <div className="text-[10px] uppercase tracking-widest">Download on the</div>
                  <div className="font-semibold text-sm font-[Outfit]">App Store</div>
                </div>
              </a>
              <a
                href={get("android")?.url || "#"}
                data-testid="download-android"
                onClick={() => trackEvent("download_app_click", { platform: "android" })}
                className="inline-flex items-center gap-3 rounded-2xl bg-white text-black px-5 py-3 hover:opacity-90 transition"
              >
                <Smartphone className="w-6 h-6" />
                <div className="leading-tight text-left">
                  <div className="text-[10px] uppercase tracking-widest">Get it on</div>
                  <div className="font-semibold text-sm font-[Outfit]">Google Play</div>
                </div>
              </a>
              <a
                href={`${API_BASE}/download/latest`}
                data-testid="download-apk"
                onClick={() => trackEvent("download_app_click", { platform: "apk", source: "download_page" })}
                className="inline-flex items-center gap-3 rounded-2xl border border-[#D4AF37] text-[#D4AF37] px-5 py-3 hover:bg-[#D4AF37] hover:text-black transition"
              >
                <Download className="w-5 h-5" />
                <div className="leading-tight text-left">
                  <div className="text-[10px] uppercase tracking-widest">Direct</div>
                  <div className="font-semibold text-sm font-[Outfit]">APK Download</div>
                </div>
              </a>
            </div>

            <div className="mt-10 flex items-center gap-6 text-slate-400 text-sm">
              <div className="flex items-center gap-1.5">
                <div className="flex">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />)}
                </div>
                <span className="ml-1.5">4.8 · 12K reviews</span>
              </div>
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                <span>Bank-grade security</span>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="relative mx-auto w-[300px] h-[600px] rounded-[3rem] border border-white/15 glass-strong overflow-hidden shadow-[0_30px_120px_rgba(14,165,233,0.25)]">
              <div className="absolute top-4 left-1/2 -translate-x-1/2 w-32 h-6 rounded-full bg-black/60" />
              <div className="absolute inset-x-6 top-16 bottom-16">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Portfolio</div>
                <div className="font-[Outfit] text-3xl text-white font-light mt-1">$248,302</div>
                <div className="text-emerald-400 text-xs mt-0.5">+22.6% YTD</div>
                <div className="mt-5 space-y-3">
                  {[
                    { t: "Mutual Funds", v: "$104,210", c: "text-sky-400" },
                    { t: "Digital Gold", v: "$48,901", c: "text-[#D4AF37]" },
                    { t: "Forex Wallet", v: "$32,440", c: "text-emerald-400" },
                    { t: "Travel Card", v: "$8,221", c: "text-rose-400" },
                  ].map((x, i) => (
                    <div key={i} className="rounded-xl border border-white/5 bg-white/5 p-3 flex items-center justify-between">
                      <div className="text-xs text-slate-300">{x.t}</div>
                      <div className={`text-sm font-medium ${x.c}`}>{x.v}</div>
                    </div>
                  ))}
                </div>
                <button className="mt-6 w-full bg-sky-500 rounded-xl py-2.5 text-sm font-medium text-white">Invest now</button>
              </div>
            </div>
            <div className="absolute -inset-8 -z-10 rounded-full bg-sky-500/10 blur-3xl" />
          </div>
        </div>
      </section>

      <section className="mfb-section border-t border-white/5">
        <div className="mfb-container grid md:grid-cols-3 gap-5">
          {[
            { t: "Lightning-fast", d: "60fps charts and instant order execution." },
            { t: "Privacy-first", d: "Biometric login + end-to-end encrypted data." },
            { t: "Offline ready", d: "View portfolios & insights even without signal." },
          ].map((x, i) => (
            <div key={i} className="card-luxury p-7" data-testid={`download-feature-${i}`}>
              <div className="text-white font-[Outfit] text-xl">{x.t}</div>
              <div className="mt-2 text-slate-400 text-sm leading-relaxed">{x.d}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
